import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  inject
} from '@angular/core';
import { Platform } from '@ionic/angular';
import { Subject, debounceTime, from, takeUntil } from 'rxjs';
import { Logger } from '@app/utils/logger';
import { UserAPIService } from '@providers/api/user/user.service';
import { Session } from '@providers/session/session.service';

/**
 * Component to sync device timezone with server.
 */
@Component({
  selector: 'app-sync-device-timezone',
  template: ``,
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SyncDeviceTimezoneComponent implements OnInit, OnDestroy {
  private readonly platform = inject(Platform);
  private readonly session = inject(Session);
  private readonly userApi = inject(UserAPIService);

  private readonly log = Logger.getInstance('SyncDeviceTimezoneComponent');

  private readonly destroyed$ = new Subject<void>();

  /**
   * Component initialization.
   */
  ngOnInit(): void {
    from(this.platform.ready())
      .pipe(
        debounceTime(3000), // Wait for 3 seconds after platform is ready
        takeUntil(this.destroyed$)
      )
      .subscribe(() => {
        const authenticated = this.session.isAuthenticated();
        if (authenticated) {
          this.log.info('User authenticated, syncing timezone...');
          this.syncTimezone();
        } else {
          this.log.info('User not authenticated, skipping timezone sync.');
        }
      });
  }

  /**
   * Component cleanup.
   */
  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  /**
   * Sync device timezone offset with server.
   */
  private syncTimezone(): void {
    // Get device timezone offset in minutes
    const deviceTimezoneOffsetMinute = new Date().getTimezoneOffset();

    // Convert to positive value (server expects positive offset for negative UTC)
    const offsetToSend = -deviceTimezoneOffsetMinute;

    this.userApi
      .updateUserDeviceTimezone({
        deviceTimezoneOffsetMinute: offsetToSend
      })
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        /**
         * Successfully synced device timezone.
         */
        next: () => {
          this.log.info('Successfully synced device timezone. Response:');
        },
        /**
         * Failed to sync device timezone.
         *
         * @param {unknown} error Error.
         */
        error: (error: unknown) => {
          this.log.error('Failed to sync device timezone:', error);
        }
      });
  }
}
